import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import api from "../Utils/api";
import Select from "react-select";
import Spinner from "../Components/Spinner";
import MapboxLocationSelector from "../Components/Location/MapboxLocationSelector";

export default function CheckpointManage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const [isLoading, setIsLoading] = useState(false);
  const [checkpoint, setCheckpoint] = useState(null);
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [codeError, setCodeError] = useState("");
  const [zipCode, setZipCode] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    type: "QR",
    locationID: "",
    code: "",
    longitude: "",
    latitude: "",
    error_range: "",
    geofenceRadius: 100,
    note: "",
  });

  useEffect(() => {
    if (id) {
      getCheckpoint();
      getLocations();
      if (user && user.deputyAccessLevel === "system_administrator") {
        getCompanies();
      }
    }
  }, [id, user]);

  const getCheckpoint = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/checkpoint/${id}`);
      setIsLoading(false);
      if (response.data.success) {
        const checkpointData = response.data.checkpoint;
        setCheckpoint(checkpointData);
        setFormData({
          name: checkpointData.name || "",
          type: checkpointData.type || "QR",
          locationID: checkpointData.locationID?._id || checkpointData.locationID || "",
          code: checkpointData.code || "",
          longitude: checkpointData.longitude || "",
          latitude: checkpointData.latitude || "",
          error_range: checkpointData.error_range || "",
          geofenceRadius: checkpointData.geofenceRadius || 100,
          note: checkpointData.note || "",
        });
        const location = checkpointData.locationID;
        if (location) {
          setSelectedLocation({ 
            _id: location._id || location, 
            name: location.name || "" 
          });
        }
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch checkpoint");
      setIsLoading(false);
    }
  };

  const getLocations = async () => {
    try {
      const response = await api.get("/location");
      if (response.data.locations) {
        setLocations(response.data.locations.filter((loc) => !loc.archive));
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch locations");
    }
  };

  const getCompanies = async () => {
    try {
      const response = await api.get("/company");
      if (response.data.success) {
        setCompanies(response.data.companies || []);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Validate NFC code format
  const validateNFCCode = (code) => {
    const nfcCodePattern = /^([A-Fa-f0-9]{2}:){6}[A-Fa-f0-9]{2}$/;
    return nfcCodePattern.test(code);
  };

  const handleCodeChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, code: value });
    
    if (formData.type === "NFC" && value) {
      if (!validateNFCCode(value)) {
        setCodeError("NFC code must be in hexadecimal format (XX:XX:XX:XX:XX:XX:XX)");
      } else {
        setCodeError("");
      }
    } else {
      setCodeError("");
    }
  };

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setFormData({ 
      ...formData, 
      type: newType,
      code: newType === "QR" ? "" : formData.code
    });
    setCodeError("");
  };

  const handleSave = async () => {
    try {
      if (!formData.name || !formData.locationID) {
        toast.error("Name and Location are required");
        return;
      }
      
      // Validate NFC code if type is NFC
      if (formData.type === "NFC") {
        if (!formData.code) {
          setCodeError("NFC code is required for NFC checkpoints");
          return;
        }
        if (!validateNFCCode(formData.code)) {
          setCodeError("NFC code must be in hexadecimal format (XX:XX:XX:XX:XX:XX:XX)");
          return;
        }
      }
      
      setIsLoading(true);
      const data = {
        ...formData,
        locationID: formData.locationID,
      };

      // Don't send code for QR checkpoints (it's auto-generated)
      if (data.type === "QR") {
        delete data.code;
      }

      const response = await api.put(`/checkpoint/${id}`, data);
      setIsLoading(false);

      if (response.data.success) {
        toast.success("Checkpoint updated successfully");
        navigate("/checkpoints");
      } else {
        toast.error(response.data.message || "Failed to update checkpoint");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update checkpoint");
      setIsLoading(false);
    }
  };

  const locationOptions = locations.map((loc) => ({
    _id: loc._id,
    name: loc.name,
    label: loc.name,
    value: loc._id,
  }));

  if (isLoading && !checkpoint) {
    return <Spinner />;
  }

  if (!checkpoint) {
    return (
      <div className="px-6 mt-20 mx-10 pb-20">
        <div className="text-center py-12">
          <p className="text-gray-500">Checkpoint not found</p>
          <button
            onClick={() => navigate("/checkpoints")}
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            Back to Checkpoints
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 mt-20 mx-10 pb-20 overflow-y-auto h-[calc(100vh-5rem)]">
      {isLoading && <Spinner />}

      {/* Header */}
      <div className="flex justify-between items-center mb-6 sticky top-0 bg-white z-10 pb-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/checkpoints")}
            className="text-gray-600 hover:text-gray-800"
          >
            <i className="fa fa-arrow-left text-xl"></i>
          </button>
          <h1 className="text-3xl font-bold text-gray-600">Edit Checkpoint</h1>
        </div>
        <button
          onClick={handleSave}
          disabled={isLoading || !formData.name || !formData.locationID || !!codeError}
          className="px-4 py-2 bg-gray-700 text-white rounded-md text-sm font-medium hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <i className="fa fa-save mr-2"></i>
          Save Changes
        </button>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="grid grid-cols-2 gap-4 px-4">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                placeholder="Checkpoint name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.type}
                onChange={handleTypeChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                <option value="QR">QR Code</option>
                <option value="NFC">NFC</option>
              </select>
            </div>

            {formData.type === "NFC" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  NFC Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={handleCodeChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                    codeError
                      ? "border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:ring-gray-500"
                  }`}
                  placeholder="XX:XX:XX:XX:XX:XX:XX"
                  maxLength={20}
                />
                {codeError && (
                  <p className="text-red-500 text-xs mt-1">{codeError}</p>
                )}
                {!codeError && formData.code && (
                  <p className="text-gray-500 text-xs mt-1">
                    Format: Hexadecimal (e.g., A1:B2:C3:D4:E5:F6:G7)
                  </p>
                )}
              </div>
            )}

            {formData.type === "QR" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  QR Code
                </label>
                <input
                  type="text"
                  value={checkpoint.code || ""}
                  disabled
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-600 cursor-not-allowed"
                />
                <p className="text-gray-500 text-xs mt-1">
                  QR codes are auto-generated (UUID format) and cannot be changed
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location <span className="text-red-500">*</span>
              </label>
              <Select
                name="location"
                placeholder="Select Location"
                options={locationOptions}
                value={selectedLocation}
                onChange={(option) => {
                  setSelectedLocation(option);
                  setFormData({ ...formData, locationID: option ? option._id : "" });
                }}
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option._id}
                isClearable={false}
                styles={{
                  control: (provided) => ({
                    ...provided,
                    borderColor: "#d1d5db",
                    boxShadow: "none",
                    "&:hover": { borderColor: "#9ca3af" },
                  }),
                }}
              />
            </div>

            {/* Map and Location Fields */}
            <div className="border-t pt-4 mt-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Checkpoint Location</h3>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Latitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                    placeholder="e.g., 53.3498"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Decimal degrees (e.g., 53.3498)
                  </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Longitude
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                    placeholder="e.g., -6.2603"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Decimal degrees (e.g., -6.2603)
                  </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Geofence Radius (meters)
                </label>
                <input
                  type="number"
                  value={formData.geofenceRadius}
                  onChange={(e) => setFormData({ ...formData, geofenceRadius: parseFloat(e.target.value) || 100 })}
                  min="10"
                  max="1000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Distance in meters for geofence validation (10-1000m)
                </p>
              </div>
            </div>
          </div>

          {/* Interactive Mapbox Map */}
          <div className="px-4">
            <MapboxLocationSelector
              latitude={formData.latitude}
              longitude={formData.longitude}
              geofenceRadius={formData.geofenceRadius}
              zipCode={zipCode}
              onLocationChange={(lat, lng) => {
                setFormData((prev) => ({
                  ...prev,
                  latitude: lat !== null ? lat.toString() : "",
                  longitude: lng !== null ? lng.toString() : "",
                }));
              }}
              onGeofenceChange={(radius) => {
                setFormData((prev) => ({
                  ...prev,
                  geofenceRadius: radius !== null ? Math.round(radius) : 100,
                }));
              }}
              onZipCodeChange={(value) => setZipCode(value)}
            />
          </div>

        </div>
      </div>
    </div>
  );
}

